import type { Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { demoMember } from "./demo.js";
import { fetchOwnMember } from "./members.js";
import { isConfigured, supabase } from "./supabase.js";
import type { Member } from "./types.js";

/**
 * Signing in and being a member are separate states (requirements §4), so this is not a
 * boolean. Every screen in the app is gated on exactly one of these.
 */
export type MembershipState =
  | "loading"
  /** No Supabase project configured. Sample data, writes refused. */
  | "demo"
  | "signed-out"
  /** Authenticated, but has not asked to join yet. */
  | "no-member"
  | "pending"
  | "rejected"
  | "suspended"
  | "active";

interface AuthValue {
  state: MembershipState;
  member: Member | null;
  userId: string | null;
  email: string | null;
  /** Google supplies one at sign-in; we store the URL and none of the bytes. */
  avatarUrl: string | null;
  isDemo: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  reloadMember: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(!isConfigured);
  const [demoSignedOut, setDemoSignedOut] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!supabase) return;

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoaded(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setSessionLoaded(true);
      // The membership row belongs to the user who just changed, so nothing cached
      // about the previous one is still true.
      queryClient.clear();
    });

    return () => listener.subscription.unsubscribe();
  }, [queryClient]);

  const userId = session?.user.id ?? null;

  const { data: member, isPending: memberPending } = useQuery({
    queryKey: ["member", userId],
    queryFn: () => fetchOwnMember(userId!),
    enabled: Boolean(userId),
    staleTime: 10_000,
  });

  const value = useMemo<AuthValue>(() => {
    const state = resolveState({
      sessionLoaded,
      userId,
      memberPending,
      member: member ?? null,
      demoSignedOut,
    });

    return {
      state,
      member: isConfigured ? (member ?? null) : demoSignedOut ? null : demoMember,
      userId,
      email:
        session?.user.email ?? (isConfigured ? null : demoSignedOut ? null : "demo@example.com"),
      avatarUrl: (session?.user.user_metadata["avatar_url"] as string | undefined) ?? null,
      isDemo: !isConfigured,
      signInWithGoogle: async () => {
        setDemoSignedOut(false);
        if (supabase) {
          await supabase.auth.signInWithOAuth({
            provider: "google",
            options: { redirectTo: window.location.origin },
          });
        }
      },
      signInWithEmail: async (email: string) => {
        setDemoSignedOut(false);
        const { error } = (await supabase?.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: window.location.origin },
        })) ?? { error: null };
        if (error) throw new Error(error.message);
      },
      signOut: async () => {
        if (supabase) {
          await supabase.auth.signOut();
        }
        setDemoSignedOut(true);
        queryClient.clear();
      },
      reloadMember: () => {
        void queryClient.invalidateQueries({ queryKey: ["member"] });
      },
    };
  }, [session, sessionLoaded, userId, member, memberPending, demoSignedOut, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function resolveState(input: {
  sessionLoaded: boolean;
  userId: string | null;
  memberPending: boolean;
  member: Member | null;
  demoSignedOut?: boolean;
}): MembershipState {
  if (!isConfigured) return input.demoSignedOut ? "signed-out" : "demo";
  if (!input.sessionLoaded) return "loading";
  if (!input.userId) return "signed-out";
  if (input.memberPending) return "loading";
  if (!input.member) return "no-member";
  return input.member.status;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

/** Role check with the hierarchy the database uses: admin > organiser > player. */
export function hasRole(member: Member | null, required: Member["role"]): boolean {
  if (!member || member.status !== "active") return false;
  const rank = { player: 1, organiser: 2, admin: 3 };
  return rank[member.role] >= rank[required];
}
